import React, { ChangeEvent, FC } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment/moment';
import { Unstable_Grid2 as Grid, Typography, Checkbox, Avatar } from '@mui/material';

import { CheckedMode, InfiniteQuery, UseCheckedState } from 'types/app';
import { ExpressActivatedUsersFragment, SearchContentSortFields, SortDirections } from 'api';
import {
  DataTable,
  DataTableActions,
  DataTableColumn,
  DataTableRowSelection,
  SocialIcon,
} from 'components';
import { SortAction, SortParam } from 'components/DataTable';
import { formatStat } from 'helpers';

import { ExpressActivatedUsersActions } from './Partials';
import theme from 'theme';
import { UserSearchesV2Filter } from './type';

interface Props extends Partial<UseCheckedState>, InfiniteQuery<ExpressActivatedUsersFragment> {
  filter: UserSearchesV2Filter;
  sortBy: SortParam;
  onSort: SortAction;
  count: number;
  refetch: () => void;
}

const styles = {
  checkbox: {
    color: theme.palette.grey[500],
  },
} as const;

const ExpressUsersTable: FC<Props> = ({
  sentinelRef,
  results,
  isExhausted,
  isInitialLoad,
  sortBy,
  onSort,
  count,
  filter,
  ...rest
}) => {
  const { t } = useTranslation();
  const { setCheckedState, checkedMode, checkedIds, refetch } = rest;
  const isAllChecked = checkedMode !== CheckedMode.OFF;
  const emails: ExpressActivatedUsersFragment[] = getResults();

  function getResults(): ExpressActivatedUsersFragment[] {
    if (filter.emails.length) {
      return results.filter(user => filter.emails.includes(user.email));
    }

    return results;
  }

  function getTotalReaches(user: ExpressActivatedUsersFragment) {
    return user.registered_networks.reduce((acc, network) => {
      return acc + network.reach;
    }, 0);
  }

  function getProfiles(user: ExpressActivatedUsersFragment) {
    const linkedin = user.registered_networks.find(network => network.provider === 'linkedin');
    const twitter = user.registered_networks.find(network => network.provider === 'twitter');

    return { linkedin, twitter };
  }

  function handleAllCheckedChange(event: ChangeEvent<HTMLInputElement>) {
    setCheckedState(event.target.checked ? CheckedMode.PAGE : CheckedMode.OFF);
  }

  function handleCheckedChange(checkedId: string, checked: boolean) {
    let nextCheckedIds = [];

    if (checkedMode) {
      setCheckedState(CheckedMode.OFF);
      nextCheckedIds = results.map(item => item.email).filter(id => id !== checkedId);
    } else if (checked) {
      nextCheckedIds = checkedIds.slice();
      nextCheckedIds.push(checkedId);
    } else {
      nextCheckedIds = checkedIds.filter(id => id !== checkedId);
    }

    setCheckedState(nextCheckedIds);
  }

  const columns: DataTableColumn<ExpressActivatedUsersFragment>[] = [
    {
      width: 48,
      headerCell: {
        padding: 'checkbox',
        render: (
          <Checkbox checked={isAllChecked} onChange={handleAllCheckedChange} sx={styles.checkbox} />
        ),
      },
      bodyCell: {
        padding: 'checkbox',
        render: user => (
          <Checkbox
            checked={isAllChecked || checkedIds.includes(user.email)}
            onChange={event => handleCheckedChange(user.email, event.target.checked)}
            sx={styles.checkbox}
          />
        ),
      },
    },
    {
      width: 56,
      headerCell: {
        render: t('common:name'),
      },
      bodyCell: {
        render: user => {
          const { linkedin, twitter } = getProfiles(user);

          return (
            <Avatar
              src={
                linkedin?.profile_image_url ||
                twitter?.profile_image_url ||
                user.registered_networks[0]?.profile_image_url
              }
            />
          );
        },
      },
    },
    {
      width: 300,
      headerCell: {
        render: null,
      },
      bodyCell: {
        render: user => {
          const { linkedin, twitter } = getProfiles(user);

          return (
            <Typography variant="subtitle2" noWrap>
              {linkedin?.profile_name ||
                twitter?.profile_name ||
                user.registered_networks[0].profile_name}
            </Typography>
          );
        },
      },
    },
    {
      width: 200,
      headerCell: {
        render: t('common:email'),
      },
      bodyCell: {
        render: user => (
          <Typography title={user.email} noWrap>
            {user.email}
          </Typography>
        ),
      },
    },
    {
      width: 150,
      headerCell: {
        render: t('express:socialAuth'),
      },
      bodyCell: {
        render: user => {
          const { linkedin, twitter } = getProfiles(user);

          return (
            <Grid container spacing={2}>
              {linkedin && (
                <Grid key={linkedin.provider}>
                  <SocialIcon provider={linkedin.provider as 'linkedin'} size={20} />
                </Grid>
              )}
              {twitter && (
                <Grid key={twitter.provider}>
                  <SocialIcon provider={twitter.provider as 'twitter'} size={16} />
                </Grid>
              )}
            </Grid>
          );
        },
      },
    },
    {
      width: 100,
      headerCell: {
        render: t('common:network'),
      },
      bodyCell: {
        render: user => {
          const reaches = getTotalReaches(user);
          const formatted = formatStat(reaches);
          return (
            <Typography title={formatted} noWrap>
              {formatted}
            </Typography>
          );
        },
      },
    },
    {
      width: 200,
      headerCell: {
        sort: {
          field: 'created_at' as SearchContentSortFields,
          direction: 'desc' as SortDirections,
        },
        render: t('common:date'),
      },
      bodyCell: {
        render: user => {
          const mom = moment.unix(user.created_at);
          const formatted = mom.format('l');
          return (
            <Typography title={formatted} noWrap>
              {formatted}
            </Typography>
          );
        },
      },
    },
  ];

  const pagination = {
    isInitialLoad,
    isExhausted,
    sentinelRef,
  };

  const actions: DataTableActions<ExpressActivatedUsersFragment> = (
    user: ExpressActivatedUsersFragment
  ) => {
    return <ExpressActivatedUsersActions variables={[user]} count={1} refetch={refetch} />;
  };

  return (
    <DataTable
      data={emails}
      columns={columns}
      actions={actions}
      pagination={pagination}
      rowSelection={
        <DataTableRowSelection
          noun={t('common:user_plural').toLowerCase()}
          count={emails.length}
          total={count}
          mode={checkedMode}
          onSelectAll={setCheckedState}
          disableMatchAll
        />
      }
      sortBy={sortBy}
      onSort={onSort}
    />
  );
};

export default ExpressUsersTable;
